/**
 * PriceAlertPanel — price-alert creation and management panel.
 *
 * Anchored to the Chart toolbar alert button.  Shows alerts for the current
 * symbol and lets the user create new ones with condition, target price,
 * optional note, and repeating flag.
 *
 * Conditions: above / below / crosses_above / crosses_below.
 * Closes on click-outside.
 */
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addAlert, removeAlert } from "@store/alert-slice";
import type { PriceAlert } from "@store/alert-slice";
import "./PriceAlertPanel.scss";

interface PriceAlertPanelProps {
  symbol: string;
  currentPrice: number;
  colorTheme: string;
  onClose: () => void;
}

const CONDITIONS = [
  { value: "above" as const, label: "Price goes above" },
  { value: "below" as const, label: "Price goes below" },
  { value: "crosses_above" as const, label: "Price crosses above" },
  { value: "crosses_below" as const, label: "Price crosses below" },
];

const PriceAlertPanel: React.FC<PriceAlertPanelProps> = ({
  symbol,
  currentPrice,
  colorTheme,
  onClose,
}) => {
  const dispatch = useDispatch();
  const allAlerts = useSelector((state: any) => state.alerts?.alerts || []) as PriceAlert[];
  const symbolAlerts = allAlerts.filter(a => a.symbol === symbol);

  const [targetPrice, setTargetPrice] = useState<string>(currentPrice?.toFixed(2) || "");
  const [condition, setCondition] = useState<PriceAlert["condition"]>("above");
  const [note, setNote] = useState("");
  const [repeating, setRepeating] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleCreateAlert = useCallback(() => {
    const price = parseFloat(targetPrice);
    if (isNaN(price) || price <= 0) return;

    dispatch(addAlert({
      symbol,
      condition,
      targetPrice: price,
      createdAtPrice: currentPrice,
      repeating,
      note: note.trim() || undefined,
    }));

    // Reset form
    setTargetPrice(currentPrice?.toFixed(2) || "");
    setNote("");
    setRepeating(false);
  }, [dispatch, symbol, condition, targetPrice, currentPrice, repeating, note]);

  const handleRemoveAlert = useCallback((alertId: string) => {
    dispatch(removeAlert(alertId));
  }, [dispatch]);

  const formatPrice = (n: number) =>
    n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const formatTime = (ts: number) => {
    const d = new Date(ts);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className={`price-alert-panel ${colorTheme}`} ref={panelRef}>
      <div className="alert-panel-header">
        <h3>Price Alerts — {symbol}</h3>
        <button className="alert-panel-close" onClick={onClose}>✕</button>
      </div>

      <div className="alert-panel-body">
        {/* Create new alert form */}
        <div className="alert-create-form">
          <div className="alert-form-row">
            <label>Condition</label>
            <select
              value={condition}
              onChange={(e) => setCondition(e.target.value as PriceAlert["condition"])}
            >
              {CONDITIONS.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div className="alert-form-row">
            <label>Target Price</label>
            <div className="alert-price-input-wrap">
              <span className="alert-price-prefix">₹</span>
              <input
                type="number"
                value={targetPrice}
                onChange={(e) => setTargetPrice(e.target.value)}
                step="0.05"
                min="0"
                placeholder="Enter price"
              />
            </div>
          </div>

          <div className="alert-form-row">
            <label>Note (optional)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., breakout level"
              maxLength={100}
            />
          </div>

          <div className="alert-form-row checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={repeating}
                onChange={(e) => setRepeating(e.target.checked)}
              />
              Repeat after trigger
            </label>
          </div>

          <div className="alert-form-row">
            <span className="current-price-hint">
              Current: ₹{formatPrice(currentPrice)}
            </span>
            <button className="alert-create-btn" onClick={handleCreateAlert}>
              Create Alert
            </button>
          </div>
        </div>

        {/* Existing alerts for this symbol */}
        {symbolAlerts.length > 0 && (
          <div className="alert-list">
            <div className="alert-list-header">
              <span>Active Alerts ({symbolAlerts.length})</span>
            </div>
            {symbolAlerts.map(alert => (
              <div key={alert.id} className={`alert-item ${alert.triggered ? "triggered" : ""}`}>
                <div className="alert-item-info">
                  <span className="alert-condition">
                    {CONDITIONS.find(c => c.value === alert.condition)?.label}
                  </span>
                  <span className="alert-target-price">₹{formatPrice(alert.targetPrice)}</span>
                  {alert.note && <span className="alert-note">{alert.note}</span>}
                  <span className="alert-meta">
                    Created {formatTime(alert.createdAt)}
                    {alert.repeating && " • Repeating"}
                  </span>
                </div>
                <button
                  className="alert-remove-btn"
                  onClick={() => handleRemoveAlert(alert.id)}
                  title="Remove alert"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {symbolAlerts.length === 0 && (
          <div className="alert-empty">
            No alerts set for {symbol}
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceAlertPanel;
